/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from "react";
import { Box, Button, MenuItem, Select, TextField, Tooltip, Typography } from "@mui/material";
import { Vocabulary } from "../../../Utils/Vocabulary";
import { urlEnum } from "../../../Utils/urlEnum";
import { postData } from "../../../Services/postData";
import { toast } from "react-toastify";
import GenericModal from "../../../Modal/GenericModal";
import { isMobile } from "../../../Utils/utils";
import styles from "./LeadStatusPipeline.module.css";

const BRAND = "#FF6B1A";

export interface LeadStatus {
  id: number;
  nume: string;
  culoare?: string;
}

interface LeadStatusPipelineProps {
  statuses: LeadStatus[];
  activeStatusId: number | undefined;
  leadId: number;
  assignedUserId: number | null | undefined;
  onStatusChanged: (newStatusId: number, cancelReason: string | null) => void;
}

export default function LeadStatusPipeline({
  statuses,
  activeStatusId,
  leadId,
  assignedUserId,
  onStatusChanged,
}: LeadStatusPipelineProps) {
  const [pendingStatus, setPendingStatus] = useState<LeadStatus | null>(null);
  const [cancelReason, setCancelReason] = useState("");
  const [saving, setSaving] = useState(false);

  const activeIdx = statuses.findIndex((s) => s.id === activeStatusId);
  const canceledStatusId = statuses.find((s) => s.nume === "Anulat")?.id;

  function handleStatusClick(s: LeadStatus) {
    if (s.id === activeStatusId) return;
    setPendingStatus(s);
    setCancelReason("");
  }

  async function handleConfirm() {
    if (!pendingStatus) return;
    if (pendingStatus.id === canceledStatusId && cancelReason.trim().length < 3) {
      toast.error(Vocabulary.cancelReasonRequired);
      return;
    }
    setSaving(true);
    try {
      await postData(
        `${urlEnum.clientMessages}/updateStatus/${leadId}/${pendingStatus.id}/${assignedUserId}`,
        { cancel_reason: cancelReason || null }
      );
      onStatusChanged(pendingStatus.id, cancelReason || null);
      setPendingStatus(null);
      toast.success(Vocabulary.successfullyUpdated);
    } catch {
      toast.error(Vocabulary.updateError);
    } finally {
      setSaving(false);
    }
  }

  const pendingColor = "#FF6B1A";

  return (
    <>
      {/* ── Pipeline bar ── */}
      <Box className={styles.pipelineWrapper}>
        {isMobile() ? (
          <Box className={styles.mobileSelect}>
            <Typography variant="caption" className={styles.mobileSelectLabel}>
              {Vocabulary.status}
            </Typography>
            <Select
              value={activeStatusId ?? ""}
              onChange={(e) => {
                const s = statuses.find((s) => s.id === Number(e.target.value));
                if (s) handleStatusClick(s);
              }}
              size="small"
              fullWidth
              sx={{
                fontSize: 13,
                fontWeight: 600,
                borderRadius: 2,
                "& .MuiOutlinedInput-notchedOutline": { borderColor: BRAND },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BRAND },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BRAND },
                color: BRAND,
              }}
            >
              {statuses.map((s) => (
                <MenuItem key={s.id} value={s.id} style={{ fontSize: 13 }}>
                  {s.nume}
                </MenuItem>
              ))}
            </Select>
          </Box>
        ) : (
          <Box className={styles.pipeline}>
            {statuses.map((s, idx) => {
              const isActive = activeStatusId === s.id;
              const isPast = activeIdx > idx;
              const textColor = isActive ? BRAND : isPast ? "#374151" : "#94a3b8";
              const fontWeight = isActive ? 700 : isPast ? 500 : 400;

              return (
                <Box key={s.id} className={styles.stepWrapper}>
                  <Tooltip title={isActive ? s.nume : `${Vocabulary.status}: ${s.nume}`}>
                    <Box
                      onClick={() => handleStatusClick(s)}
                      className={`${styles.step} ${isActive ? styles.stepActive : ""}`}
                      style={isActive ? { backgroundColor: "rgba(255, 107, 26, 0.06)" } : undefined}
                    >
                      <Typography
                        variant="caption"
                        className={styles.label}
                        style={{ color: textColor, fontWeight }}
                      >
                        {s.nume}
                      </Typography>
                    </Box>
                  </Tooltip>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      {/* ── Confirmation modal ── */}
      <GenericModal
        open={!!pendingStatus}
        onClose={() => setPendingStatus(null)}
        title=""
        maxWidth={600}
        buttons={
          <Box className={styles.modalActions}>
            <Button
              variant="outlined"
              onClick={() => setPendingStatus(null)}
              disabled={saving}
              className={styles.cancelButton}
            >
              {Vocabulary.cancel}
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={saving}
              className={styles.confirmButton}
              style={{ backgroundColor: pendingColor }}
            >
              {Vocabulary.confirm}
            </Button>
          </Box>
        }
      >
        {/* Colored top bar */}
        <Box
          className={styles.modalColorBar}
          style={{ background: `linear-gradient(90deg, ${pendingColor}, ${pendingColor}88)` }}
        />

        {/* Badge + title */}
        <Box className={styles.modalTitleRow}>
          <Box
            className={styles.modalBadge}
            style={{
              backgroundColor: `${pendingColor}22`,
              borderColor: `${pendingColor}66`,
            }}
          >
            <Box className={styles.modalBadgeDot} style={{ backgroundColor: pendingColor }} />
          </Box>
          <Box>
            <Typography className={styles.modalStatusName}>{pendingStatus?.nume}</Typography>
            <Typography className={styles.modalSubtitle}>Schimbare status</Typography>
          </Box>
        </Box>

        <Typography className={styles.modalBody}>
          {Vocabulary.confirmStatusChangeTo}{" "}
          <strong style={{ color: pendingColor }}>{pendingStatus?.nume}</strong>?
        </Typography>

        {pendingStatus?.id === canceledStatusId && (
          <TextField
            fullWidth
            multiline
            rows={3}
            size="small"
            label="Motiv anulare (min. 3 caractere)"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            error={cancelReason.length > 0 && cancelReason.trim().length < 3}
            helperText={
              cancelReason.length > 0 && cancelReason.trim().length < 3
                ? `${3 - cancelReason.trim().length} caractere în plus necesare`
                : ""
            }
            autoFocus
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
          />
        )}
      </GenericModal>
    </>
  );
}
